import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Button from "@/js/common/ui/Button";
import DatePickerBox from "@/js/common/ui/input/DatePickerBox";
import SelectBox from "@/js/common/ui/input/SelectBox";
import TextBox from "@/js/common/ui/input/TextBox";
import Util from "@/js/common/Util";
import * as Atom from "@/js/common/Atom";
import React from "react";
import { useRecoilValue } from "recoil";

interface Props {
    setModalOpen: Function;
    setCallTime: Function;
}

const MbrasExistingMngDialog = ({ setModalOpen, setCallTime }: Props) => {
    const userInfo = useRecoilValue(Atom.userInfo);

    const [step           , setStep]            = React.useState(1);
    const [mbrasNm        , setMbrasNm]         = React.useState<string>("");
    const [telno          , setTelno]           = React.useState<string>("");
    const [brdtDate       , setBrdtDate]        = React.useState<string>("");
    const [gndrCd         , setGndrCd]          = React.useState<string>("");
    const [upOgnzCd       , setUpOgnzCd]        = React.useState<string>("");
    const [ogdpOrgCd      , setOgdpOrgCd]       = React.useState<string>("");
    const [lbrShpSeCd     , setLbrShpSeCd]      = React.useState<string>("");
    const [ocptTypeCd     , setOcptTypeCd]      = React.useState<string>("");
    const [wtjrNm         , setWtjrNm]          = React.useState<string>("");
    const [frstJncmpYmd   , setFrstJncmpYmd]    = React.useState<string>("");
    const [joinYmd        , setJoinYmd]         = React.useState<string>("");
    const [aprvYmd        , setAprvYmd]         = React.useState<string>("");
    const [addr           , setAddr]            = React.useState<string>("");
    const [memo           , setMemo]            = React.useState<string>("");
    const [mbrasId        , setMbrasId]         = React.useState<string>("");

    React.useEffect(() => {
        if (userInfo.ognzCd !== "00_000") setUpOgnzCd(userInfo.upOgnzCd);
        if (!userInfo.ognzCd.includes("000")) setOgdpOrgCd(userInfo.ognzCd);
    }, [userInfo]);

    const ocptGroupCd = lbrShpSeCd === "01" ? "PMEMP_OCPT_TYPE_CD" : lbrShpSeCd === "02" ? "IREMP_OCPT_TYPE_CD" : "";

    const getCodeName = (groupCd: string, code: string) => {
        const item = Util.getCodeArray(groupCd).find((data: any) => data.code === code);
        return item ? item.name : "-";
    };

    const getUpOgnzName = (code: string) => {
        const item = Util.getUpOrgCodeArray("01").find((data: any) => data.code === code);
        return item ? item.name : "-";
    };

    const getOgnzName = (upCode: string, code: string) => {
        const item = Util.getDownOrgCodeArray(upCode).find((data: any) => data.code === code);
        return item ? item.name : "-";
    };

    // 필수값 확인
    const openCheckAlert = (title: string, msg: string) => {
        AppContext.openAlert({ title: title, msg: msg, icon: "warning" });
    };

    // STEP 1. 조합원확인
    const handleDuplicateCheck = () => {
        if (Util.isEmpty(mbrasNm.trim())) { openCheckAlert("성명 확인", "성명을 입력해주세요."); return; }
        if (Util.isEmpty(telno)) { openCheckAlert("전화번호 확인", "전화번호를 입력해주세요."); return; }
        if (!Util.chkPhonePtn(telno)) { openCheckAlert("전화번호 확인", "전화번호를 올바르게 입력해주세요."); return; }

        AppContext.showSpinner();
        Axios.dataAccess({
            url         : "system/mbras/existing/select-mbras-existing-dup-chk.do",
            methodType  : "post",
            paramType   : "json",
            paramData   : { mbrasNm: mbrasNm.trim(), telno: telno },
            onFailedFn  : () => AppContext.hideSpinner(),
            onSuccessFn : (res: any) => {
                AppContext.hideSpinner();

                const item = res.result.item;

                if (Number(item.activeDupCnt || 0) > 0) {
                    openCheckAlert("중복 등록 불가", "동일한 성명과 전화번호의 유효 조합원이 이미 등록되어 있습니다.");
                    return;
                }

                setStep(2);
            },
        });
    };

    // STEP 2. 조합원 · 가입정보 확인
    const handleInfoCheck = () => {
        if (Util.isEmpty(brdtDate)) { openCheckAlert("생년월일 확인", "생년월일을 입력해주세요."); return; }
        if (Util.isEmpty(gndrCd)) { openCheckAlert("성별 확인", "성별을 선택해주세요."); return; }
        if (Util.isEmpty(upOgnzCd)) { openCheckAlert("소속본부 확인", "소속본부를 선택해주세요."); return; }
        if (Util.isEmpty(ogdpOrgCd)) { openCheckAlert("소속지부 확인", "소속지부를 선택해주세요."); return; }
        if (Util.isEmpty(lbrShpSeCd)) { openCheckAlert("근로형태 확인", "근로형태를 선택해주세요."); return; }
        if (Util.isEmpty(ocptTypeCd)) { openCheckAlert("직종 확인", "직종을 선택해주세요."); return; }
        if (lbrShpSeCd === "01" && Util.isEmpty(frstJncmpYmd)) { openCheckAlert("입직년월일 확인", "입직년월일을 입력해주세요."); return; }
        if (Util.isEmpty(joinYmd)) { openCheckAlert("가입신청일자 확인", "가입신청일자를 입력해주세요."); return; }
        if (Util.isEmpty(aprvYmd)) { openCheckAlert("가입승인일자 확인", "가입승인일자를 입력해주세요."); return; }
        if (joinYmd > Util.dateToString(new Date(), "")) { openCheckAlert("가입신청일자 확인", "가입신청일자는 현재 날짜보다 늦을 수 없습니다."); return; }
        if (aprvYmd > Util.dateToString(new Date(), "")) { openCheckAlert("가입승인일자 확인", "가입승인일자는 현재 날짜보다 늦을 수 없습니다."); return; }
        if (aprvYmd < joinYmd) { openCheckAlert("가입승인일자 확인", "가입승인일자는 가입신청일자보다 빠를 수 없습니다."); return; }
        if (Util.isEmpty(addr.trim())) { openCheckAlert("주소 확인", "주소를 입력해주세요."); return; }

        setStep(3);
    };

    // STEP 3. 최종 등록
    const handleSave = () => {
        AppContext.showSpinner();
        Axios.dataAccess({
            url         : "system/mbras/existing/save-mbras-existing.do",
            methodType  : "post",
            paramType   : "json",
            paramData   : {
                mbrasNm        : mbrasNm.trim(),
                telno          : telno,
                brdtDate       : brdtDate,
                gndrCd         : gndrCd,
                upOgnzCd       : upOgnzCd,
                ogdpOrgCd      : ogdpOrgCd,
                lbrShpSeCd     : lbrShpSeCd,
                ocptTypeCd     : ocptTypeCd,
                wtjrNm         : wtjrNm,
                frstJncmpYmd   : lbrShpSeCd === "01" ? frstJncmpYmd : "",
                joinYmd        : joinYmd,
                aprvYmd        : aprvYmd,
                addr           : addr.trim(),
                memo           : memo,
            },
            onFailedFn  : () => AppContext.hideSpinner(),
            onSuccessFn : (res: any) => {
                AppContext.hideSpinner();
                setMbrasId(res.result.mbrasId || "");
                setCallTime(new Date());
                setStep(4);
            },
        });
    };

    const summaryLabelStyle = { width: "100px" };
    const summaryValueStyle = { width: "190px" };

    return (
        <div className="w100p">

            {/* 단계 표시 */}
            {step < 4 && (
                <div className="w630 m0auto pt5 pb20">
                    <div className="bgcFAFAFA bdrd8 bd1 bdcDDDDDD p15">
                        <div className="dpFlx aiC jcSb mb15">
                            <div className="fm-b fs15 fc111111">조합원 등록 순서</div>
                            <div className="fm-c fs12 fc777777">STEP {step} / 3</div>
                        </div>

                        <div className="dpFlx aiC jcC">
                            <div className="w140 taC">
                                <div className={"w28 h28 m0auto dpFlx aiC jcC fm-b fs12 bdrd50p " + (step >= 1 ? "bgc007bff fcWhite" : "bgcEDEDED fc999999")}>1</div>
                                <div className={"fm-b fs12 mt5 " + (step >= 1 ? "fc007bff" : "fc999999")}>조합원확인</div>
                            </div>

                            <div className={"w70 h2 " + (step >= 2 ? "bgc007bff" : "bgcDEDEDE")}></div>

                            <div className="w140 taC">
                                <div className={"w28 h28 m0auto dpFlx aiC jcC fm-b fs12 bdrd50p " + (step >= 2 ? "bgc007bff fcWhite" : "bgcEDEDED fc999999")}>2</div>
                                <div className={"fm-b fs12 mt5 " + (step >= 2 ? "fc007bff" : "fc999999")}>조합원 · 가입정보</div>
                            </div>

                            <div className={"w70 h2 " + (step >= 3 ? "bgc007bff" : "bgcDEDEDE")}></div>

                            <div className="w140 taC">
                                <div className={"w28 h28 m0auto dpFlx aiC jcC fm-b fs12 bdrd50p " + (step >= 3 ? "bgc007bff fcWhite" : "bgcEDEDED fc999999")}>3</div>
                                <div className={"fm-b fs12 mt5 " + (step >= 3 ? "fc007bff" : "fc999999")}>최종확인</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* STEP 1. 조합원확인 */}
            {step === 1 && (
                <div className="w630 m0auto">
                    <div className="bd1 bdcDDDDDD bdrd8 p20">
                        <div className="fm-b fs18 fc111111">조합원정보 확인</div>
                        <div className="fm-c fs12 fc777777 mt5">기존 등록 여부를 먼저 확인한 후 조합원 정보를 입력합니다.</div>

                        <div className="bgcFAFAFA bdrd6 p20 mt20">
                            <div className="w540 m0auto dpFlx aiC">
                                <div className="w90 fm-b fs13 fc111111">성명</div>
                                <TextBox value={mbrasNm} width={450} height={40} fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1} borderColor="#ddd" borderOnColor="#222" borderRadius={5} style={{ backgroundColor: "#fff", padding: "0 10px" }} placeholder="성명을 입력하세요." onChangeFunc={(text: string) => setMbrasNm(text)} />
                            </div>

                            <div className="w540 m0auto dpFlx aiC mt10">
                                <div className="w90 fm-b fs13 fc111111">전화번호</div>
                                <TextBox value={Util.setPhoneNum(telno)} type="phone" width={450} height={40} fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1} borderColor="#ddd" borderOnColor="#222" borderRadius={5} style={{ backgroundColor: "#fff", padding: "0 10px" }} placeholder="휴대전화번호를 입력하세요." iconEl={<img onClick={() => setTelno("")} className={"csP " + (Util.isEmpty(telno) ? "dpNone" : "")} src={Util.getImgSrc("common/icon_cancel_fill_18.png")} alt="icon_cancel_fill_18" />} onChangeFunc={(text: string) => setTelno(Util.unHypen(text))} />
                            </div>
                        </div>

                        <div className="mt15 fm-c fs12 fc777777 taC">성명과 휴대전화번호를 기준으로 기존 조합원을 확인합니다.</div>
                    </div>

                    <div className="dpFlx jcC mt20 gap10 mb10">
                        <Button width={100} height={40} fontFamily="fm-b" fontSize={16} letterSpacing={-1} variant="styCancelBtn" borderRadius={6} buttonClass="dpFlx aiC jcC" buttonName="닫기" onClickFunc={() => setModalOpen(false)} />
                        <Button width={130} height={40} fontFamily="fm-b" fontSize={16} letterSpacing={-1} variant="styCompleteBtn" borderRadius={6} buttonClass="dpFlx aiC jcC" buttonName="조합원 확인" onClickFunc={handleDuplicateCheck} />
                    </div>
                </div>
            )}

            {/* STEP 2. 조합원 · 가입정보 */}
            {step === 2 && (
            <div className="w630 m0auto">
                <div className="bgcFAFAFA bd1 bdcDDDDDD bdrd8 p15 mb15">
                    <div className="dpFlx aiC jcSb">
                        <div>
                            <div className="fm-b fs14 fc007bff">확인 완료</div>
                            <div className="fm-c fs12 fc777777 mt3">등록 가능한 조합원입니다.</div>
                        </div>
                        <div className="taR">
                            <div className="fm-b fs15 fc111111">{mbrasNm}</div>
                            <div className="fm-c fs12 fc777777 mt2">{Util.setPhoneNum(telno)}</div>
                        </div>
                    </div>
                </div>

                <div className="bd1 bdcDDDDDD bdrd8 p20">
                    <div className="fm-b fs18 fc111111 mb20">조합원 · 가입정보 입력</div>

                    {/* 생년월일 / 성별 */}
                    <div className="dpFlx flxNowrap gap20 jcC">
                        <div className="w280 dpFlx aiC">
                            <div className="w80 fm-b fs13 fc111111">생년월일</div>
                            <DatePickerBox value={brdtDate} width={200} height={36} fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1} borderColor="#ddd" borderOnColor="#222" borderRadius={5} bgColor="white" textAlign="left" calendarThemeColor={AppContext.themeColor} onChange={(data: string) => setBrdtDate(data)} />
                        </div>

                        <div className="w280 dpFlx aiC">
                            <div className="w80 fm-b fs13 fc111111">성별</div>
                            <SelectBox value={gndrCd} width={200} height={36} fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1} borderColor="#ddd" borderOnColor="#222" borderRadius={5} background="#fff" codeArray={Util.getCodeArray("GNDR_CD")} placeholder="성별 선택" paddingRight={8} onChangeFunc={(data: string) => setGndrCd(data)} />
                        </div>
                    </div>

                    {/* 소속본부 / 근로형태 */}
                    <div className="dpFlx flxNowrap gap20 mt10 jcC">
                        <div className="w280 dpFlx aiC">
                            <div className="w80 fm-b fs13 fc111111">소속본부</div>
                            <SelectBox value={upOgnzCd} width={200} height={36} fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1} borderColor="#ddd" borderOnColor="#222" borderRadius={5} background={userInfo.ognzCd === "00_000" ? "#fff" : "#f6f6f6"} codeArray={Util.getUpOrgCodeArray("01").slice(1)} placeholder="본부 선택" paddingRight={8} disabled={userInfo.ognzCd !== "00_000"} onChangeFunc={(data: string) => { setUpOgnzCd(data); setOgdpOrgCd(""); }} />
                        </div>

                        <div className="w280 dpFlx aiC">
                            <div className="w80 fm-b fs13 fc111111">근로형태</div>
                            <SelectBox value={lbrShpSeCd} width={200} height={36} fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1} borderColor="#ddd" borderOnColor="#222" borderRadius={5} background="#fff" codeArray={Util.getCodeArray("LBR_SHP_SE_CD")} placeholder="근로형태 선택" paddingRight={8} onChangeFunc={(data: string) => { setLbrShpSeCd(data); setOcptTypeCd(""); if (data !== "01") setFrstJncmpYmd(""); }} />
                        </div>
                    </div>

                    {/* 소속지부 / 직종 */}
                    <div className="dpFlx flxNowrap gap20 mt10 jcC">
                        <div className="w280 dpFlx aiC">
                            <div className="w80 fm-b fs13 fc111111">소속지부</div>
                            <SelectBox value={ogdpOrgCd} width={200} height={36} fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1} borderColor="#ddd" borderOnColor="#222" borderRadius={5} background={userInfo.ognzCd.includes("000") ? "#fff" : "#f6f6f6"} codeArray={Util.getDownOrgCodeArray(upOgnzCd)} placeholder="지부 선택" paddingRight={8} disabled={!userInfo.ognzCd.includes("000")} onChangeFunc={(data: string) => setOgdpOrgCd(data)} />
                        </div>

                        <div className="w280 dpFlx aiC">
                            <div className="w80 fm-b fs13 fc111111">직종</div>
                            <SelectBox value={ocptTypeCd} width={200} height={36} fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1} borderColor="#ddd" borderOnColor="#222" borderRadius={5} background="#fff" codeArray={ocptGroupCd ? Util.getCodeArray(ocptGroupCd) : []} placeholder={Util.isEmpty(lbrShpSeCd) ? "근로형태를 먼저 선택" : "직종 선택"} paddingRight={8} disabled={Util.isEmpty(lbrShpSeCd) || Util.isEmpty(ocptGroupCd)} onChangeFunc={(data: string) => setOcptTypeCd(data)} />
                        </div>
                    </div>

                    {/* 관내 / 입직년월일 */}
                    {lbrShpSeCd === "01" && (
                        <div className="dpFlx flxNowrap gap20 mt10 jcC">
                            <div className="w280 dpFlx aiC">
                                <div className="w80 fm-b fs13 fc111111">관내</div>
                                <TextBox value={wtjrNm} width={200} height={36} fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1} borderColor="#ddd" borderOnColor="#222" borderRadius={5} style={{ backgroundColor: "#fff", padding: "0 10px" }} placeholder="관내를 입력하세요." onChangeFunc={(text: string) => setWtjrNm(text)} />
                            </div>

                            <div className="w280 dpFlx aiC">
                                <div className="w80">
                                    <div className="fm-b fs13 fc111111">입직년월일</div>
                                    <div className="fm-c fs10 fc4A90A4 mt2">정규직 임용일자</div>
                                </div>
                                <DatePickerBox value={frstJncmpYmd} width={200} height={36} fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1} borderColor="#ddd" borderOnColor="#222" borderRadius={5} bgColor="white" textAlign="left" calendarThemeColor={AppContext.themeColor} onChange={(data: string) => setFrstJncmpYmd(data)} />
                            </div>
                        </div>
                    )}

                    {/* 비정규직 관내 */}
                    {lbrShpSeCd !== "01" && (
                        <div className="dpFlx flxNowrap gap20 mt10 jcC">
                            <div className="w280 dpFlx aiC">
                                <div className="w80 fm-b fs13 fc111111">관내</div>
                                <TextBox value={wtjrNm} width={200} height={36} fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1} borderColor="#ddd" borderOnColor="#222" borderRadius={5} style={{ backgroundColor: "#fff", padding: "0 10px" }} placeholder="관내를 입력하세요." onChangeFunc={(text: string) => setWtjrNm(text)} />
                            </div>
                            <div className="w280"></div>
                        </div>
                    )}

                    {/* 가입신청일자 / 가입승인일자 */}
                    <div className="dpFlx flxNowrap gap20 mt10 jcC">
                        <div className="w280 dpFlx aiC">
                            <div className="w80 fm-b fs13 fc111111">가입신청일자</div>
                            <DatePickerBox value={joinYmd} width={200} height={36} fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1} borderColor="#ddd" borderOnColor="#222" borderRadius={5} bgColor="white" textAlign="left" calendarThemeColor={AppContext.themeColor} onChange={(data: string) => setJoinYmd(data)} />
                        </div>

                        <div className="w280 dpFlx aiC">
                            <div className="w80 fm-b fs13 fc111111">가입승인일자</div>
                            <DatePickerBox value={aprvYmd} width={200} height={36} fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1} borderColor="#ddd" borderOnColor="#222" borderRadius={5} bgColor="white" textAlign="left" calendarThemeColor={AppContext.themeColor} onChange={(data: string) => setAprvYmd(data)} />
                        </div>
                    </div>

                    {/* 주소 */}
                    <div className="w580 m0auto dpFlx aiC mt10">
                        <div className="w80 fm-b fs13 fc111111">주소</div>
                        <TextBox value={addr} width={500} height={36} fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1} borderColor="#ddd" borderOnColor="#222" borderRadius={5} style={{ backgroundColor: "#fff", padding: "0 10px" }} placeholder="주소를 입력하세요." onChangeFunc={(text: string) => setAddr(text)} />
                    </div>

                    {/* 메모 */}
                    <div className="w580 m0auto dpFlx mt10">
                        <div className="w80 fm-b fs13 fc111111 pt8">메모</div>
                        <textarea value={memo} className="w500 h46 fm-c fs14 fc333333 bdrd5 bd1 bdcDDDDDD p10" style={{ boxSizing: "border-box", resize: "none", backgroundColor: "#fff" }} placeholder="조합원 관련 메모를 입력하세요" onChange={(e) => setMemo(e.target.value)} />
                    </div>

                    <div className="mt12 fm-c fs12 fc4A90A4 taC">가입신청일자와 가입승인일자는 기존 조합원의 실제 가입 이력을 기준으로 입력합니다.</div>
                </div>

                <div className="dpFlx jcC mt20 gap10 mb10">
                    <Button width={100} height={40} fontFamily="fm-b" fontSize={16} letterSpacing={-1} variant="styCancelBtn" borderRadius={6} buttonClass="dpFlx aiC jcC" buttonName="이전" onClickFunc={() => setStep(1)} />
                    <Button width={120} height={40} fontFamily="fm-b" fontSize={16} letterSpacing={-1} variant="styCompleteBtn" borderRadius={6} buttonClass="dpFlx aiC jcC" buttonName="다음" onClickFunc={handleInfoCheck} />
                </div>
            </div>
        )}

            {/* STEP 3. 최종확인 */}
            {step === 3 && (
                <div className="w630 m0auto">
                    <div className="bd1 bdcDDDDDD bdrd8 p20">
                        <div className="fm-b fs18 fc111111">등록정보 최종 확인</div>
                        <div className="fm-c fs12 fc777777 mt5 mb20">아래 정보로 조합원을 등록합니다. 등록 전 내용을 다시 확인해주세요.</div>

                        <div className="bgcFAFAFA bdrd6 p15">
                            <div className="fm-b fs14 fc007bff mb15">기본정보</div>

                            <div className="dpFlx mb10">
                                <div className="fm-c fs13 fc777777" style={summaryLabelStyle}>성명</div>
                                <div className="fm-c fs13 fc333333" style={summaryValueStyle}>{mbrasNm}</div>
                                <div className="fm-c fs13 fc777777" style={summaryLabelStyle}>전화번호</div>
                                <div className="fm-c fs13 fc333333">{Util.setPhoneNum(telno)}</div>
                            </div>

                            <div className="dpFlx">
                                <div className="fm-c fs13 fc777777" style={summaryLabelStyle}>생년월일</div>
                                <div className="fm-c fs13 fc333333" style={summaryValueStyle}>{Util.cvtDateStrForm(brdtDate, "-", "YYYYMMDD")}</div>
                                <div className="fm-c fs13 fc777777" style={summaryLabelStyle}>성별</div>
                                <div className="fm-c fs13 fc333333">{getCodeName("GNDR_CD", gndrCd)}</div>
                            </div>
                        </div>

                        <div className="bgcFAFAFA bdrd6 p15 mt12">
                            <div className="fm-b fs14 fc007bff mb15">조합원 · 가입정보</div>

                            <div className="dpFlx mb10">
                                <div className="fm-c fs13 fc777777" style={summaryLabelStyle}>본부</div>
                                <div className="fm-c fs13 fc333333" style={summaryValueStyle}>{getUpOgnzName(upOgnzCd)}</div>
                                <div className="fm-c fs13 fc777777" style={summaryLabelStyle}>지부</div>
                                <div className="fm-c fs13 fc333333">{getOgnzName(upOgnzCd, ogdpOrgCd)}</div>
                            </div>

                            <div className="dpFlx mb10">
                                <div className="fm-c fs13 fc777777" style={summaryLabelStyle}>근로형태</div>
                                <div className="fm-c fs13 fc333333" style={summaryValueStyle}>{getCodeName("LBR_SHP_SE_CD", lbrShpSeCd)}</div>
                                <div className="fm-c fs13 fc777777" style={summaryLabelStyle}>직종</div>
                                <div className="fm-c fs13 fc333333">{getCodeName(ocptGroupCd, ocptTypeCd)}</div>
                            </div>

                            <div className="dpFlx mb10">
                                <div className="fm-c fs13 fc777777" style={summaryLabelStyle}>관내</div>
                                <div className="fm-c fs13 fc333333" style={summaryValueStyle}>{Util.isEmpty(wtjrNm) ? "-" : wtjrNm}</div>
                                <div className="fm-c fs13 fc777777" style={summaryLabelStyle}>가입상태</div>
                                <div className="fm-c fs13 fc333333">가입</div>
                            </div>

                            {lbrShpSeCd === "01" && (
                                <div className="dpFlx mb10">
                                    <div className="fm-c fs13 fc777777" style={summaryLabelStyle}>입직년월일</div>
                                    <div className="fm-c fs13 fc333333" style={summaryValueStyle}>{Util.cvtDateStrForm(frstJncmpYmd, "-", "YYYYMMDD")}</div>
                                    <div className="fm-c fs13 fc777777" style={summaryLabelStyle}>가입신청일자</div>
                                    <div className="fm-c fs13 fc333333">{Util.cvtDateStrForm(joinYmd, "-", "YYYYMMDD")}</div>
                                </div>
                            )}

                            {lbrShpSeCd !== "01" && (
                                <div className="dpFlx mb10">
                                    <div className="fm-c fs13 fc777777" style={summaryLabelStyle}>가입신청일자</div>
                                    <div className="fm-c fs13 fc333333" style={summaryValueStyle}>{Util.cvtDateStrForm(joinYmd, "-", "YYYYMMDD")}</div>
                                    <div className="fm-c fs13 fc777777" style={summaryLabelStyle}>가입승인일자</div>
                                    <div className="fm-c fs13 fc333333">{Util.cvtDateStrForm(aprvYmd, "-", "YYYYMMDD")}</div>
                                </div>
                            )}

                            {lbrShpSeCd === "01" && (
                                <div className="dpFlx mb10">
                                    <div className="fm-c fs13 fc777777" style={summaryLabelStyle}>가입승인일자</div>
                                    <div className="fm-c fs13 fc333333" style={summaryValueStyle}>{Util.cvtDateStrForm(aprvYmd, "-", "YYYYMMDD")}</div>
                                    <div className="fm-c fs13 fc777777" style={summaryLabelStyle}>원천징수</div>
                                    <div className="fm-c fs13 fc333333">동의</div>
                                </div>
                            )}

                            {lbrShpSeCd !== "01" && (
                                <div className="dpFlx mb10">
                                    <div className="fm-c fs13 fc777777" style={summaryLabelStyle}>원천징수</div>
                                    <div className="fm-c fs13 fc333333">동의</div>
                                </div>
                            )}

                            <div className="dpFlx">
                                <div className="fm-c fs13 fc777777" style={summaryLabelStyle}>주소</div>
                                <div className="fm-c fs13 fc333333">{addr}</div>
                            </div>
                        </div>

                        {!Util.isEmpty(memo) && (
                            <div className="bgcFAFAFA bdrd6 p15 mt12">
                                <div className="fm-b fs14 fc007bff mb8">메모</div>
                                <div className="fm-c fs13 fc333333">{memo}</div>
                            </div>
                        )}
                    </div>

                    <div className="dpFlx jcC mt20 gap10 mb10">
                        <Button width={100} height={40} fontFamily="fm-b" fontSize={16} letterSpacing={-1} variant="styCancelBtn" borderRadius={6} buttonClass="dpFlx aiC jcC" buttonName="이전" onClickFunc={() => setStep(2)} />
                        <Button width={130} height={40} fontFamily="fm-b" fontSize={16} letterSpacing={-1} variant="styCompleteBtn" borderRadius={6} buttonClass="dpFlx aiC jcC" buttonName="조합원 등록" onClickFunc={handleSave} />
                    </div>
                </div>
            )}

            {/* 등록 완료 */}
            {step === 4 && (
                <div className="w630 m0auto">
                    <div className="bd1 bdcDDDDDD bdrd8 p20 taC">
                        <div className="w50 h50 m0auto dpFlx aiC jcC fm-b fs24 bdrd50p bgc007bff fcWhite">✓</div>
                        <div className="fm-b fs22 fc111111 mt15">조합원 등록 완료</div>
                        <div className="fm-c fs13 fc777777 mt7">{mbrasNm} 조합원이 정상적으로 등록되었습니다.</div>

                        <div className="w420 m0auto bgcFAFAFA bdrd6 p20 mt20 taL">
                            <div className="dpFlx mb10">
                                <div className="w110 fm-c fs13 fc777777">조합원 ID</div>
                                <div className="fm-b fs14 fc333333">{mbrasId}</div>
                            </div>
                            <div className="dpFlx mb10">
                                <div className="w110 fm-c fs13 fc777777">성명</div>
                                <div className="fm-c fs14 fc333333">{mbrasNm}</div>
                            </div>
                            <div className="dpFlx mb10">
                                <div className="w110 fm-c fs13 fc777777">가입신청일자</div>
                                <div className="fm-b fs14 fc333333">{Util.cvtDateStrForm(joinYmd, "-", "YYYYMMDD")}</div>
                            </div>
                            <div className="dpFlx">
                                <div className="w110 fm-c fs13 fc777777">가입승인일자</div>
                                <div className="fm-b fs14 fc333333">{Util.cvtDateStrForm(aprvYmd, "-", "YYYYMMDD")}</div>
                            </div>
                        </div>
                    </div>

                    <div className="dpFlx jcC mt20 gap10 mb10">
                        <Button width={100} height={40} fontFamily="fm-b" fontSize={16} letterSpacing={-1} variant="styCancelBtn" borderRadius={6} buttonClass="dpFlx aiC jcC" buttonName="닫기" onClickFunc={() => setModalOpen(false)} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default MbrasExistingMngDialog;